import { isUndefined } from 'lodash';
import CellAnnotationValueViewer from 'components/CellAnnotationValueViewer';
import type { CellAnnotationCapabilities } from 'components/CellAnnotationValueViewer';
import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import type { CellAnnotationConfig } from 'components/CellAnnotationValueViewer/types';
import GridRowLabel from 'components/Grid/GridRowLabel/GridRowLabel';
import { ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import { addClassName } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { AmountValueViewerWithChip, getCashFlowColumns } from './CashFlowColumnConfig';
import { getRowClassByVirtual } from './constants';
import { CashFlowLedgerCellMap, CashFlowLedgerItem, CashFlowLedgerRowData } from './types';

// Pre-FX is single_cell with no metadata columns — the popout owns its own
// fields (amount + source currency). One config per parser keeps the wiring
// allocation-free (object identity is stable across rows).
const PRE_FX_CONFIG: CellAnnotationConfig = {
  annotationType: 'pre_fx_currency',
  rowMode: 'single_cell',
  columns: [],
};

// Hover-bar opt-ins for CFL Amount cells. Only Pre-FX is enabled today; add
// `'cross_reference'` once the read-side hydration / display for cross-ref
// on CFL rows lands. The CFL writable already accepts `cell_annotations`,
// so flipping this list is the only change needed at that point.
const MONEY_BAR_CAPABILITIES: CellAnnotationCapabilities = {
  enabled: ['pre_fx_currency'],
};

export const CashFlowParser = (params: {
  rowConfig: CashFlowLedgerItem[];
  columns?: ReturnType<typeof getCashFlowColumns>;
  // Optional preview callback. When provided, each Pre-FX-eligible AMOUNT
  // cell receives a per-row `onPreFxHostPreview` bound to its own cellKey
  // so the page can patch the cell directly through `setCells`. Going by
  // cellKey (not row id) lets the page bypass `cashFlows` state, which
  // lags the spreadsheet's live cell map and would clobber other in-progress
  // edits if used as the writeback channel.
  onPreFxHostPreview?: (cellKey: string, value: number | null) => void;
}) => {
  const { rowConfig, columns, onPreFxHostPreview } = params;
  const colConfig = columns ?? getCashFlowColumns();
  const alphabet = alphabetGenerator([], colConfig.length);
  const cells: CashFlowLedgerCellMap = {};

  colConfig.forEach((col, colIndex) => {
    const columnLegend = alphabet[colIndex];
    const cellKey = `${columnLegend}1`;
    cells[cellKey] = {
      // valueViewer: (props: any) => <GridRowLabel {...props} />,
      className: ' division-bottom-only text-right',
      value: col.value,
      key: cellKey,
      alias: col.alias,
      columnLegend,
      rowNumber: 1,
      expr: col.value,
    };
  });

  // Create data rows (starting from row 2)
  // rowConfig contains the actual cash flow items (each item is a row)
  rowConfig.forEach((cashFlowItem, rowIndex: number) => {
    const rowNumber = rowIndex + 1; // +2 because row 1 is header

    if (rowIndex === 0) {
      return;
    }

    // colConfig contains the field definitions (each field is a column)
    colConfig.forEach((col, colIndex) => {
      const columnLegend = alphabet[colIndex];
      const cellKey = `${columnLegend}${rowNumber}`;

      // Get the value from the cash flow item using the column alias.
      // `defaultValue` is no longer set on any column (currency is per-row);
      // cast to keep the generic fallback shape in case future columns reintroduce one.
      let value = !isUndefined(cashFlowItem[col.alias as keyof CashFlowLedgerItem])
        ? cashFlowItem[col.alias as keyof CashFlowLedgerItem]
        : ((col as { defaultValue?: any }).defaultValue ?? null);

      if (col.alias === 'owner') {
        if (cashFlowItem.owner_fund) {
          value = `fund-${cashFlowItem.owner_fund}`;
        } else if (cashFlowItem.owner_company) {
          value = `company-${cashFlowItem.owner_company}`;
        }
      } else if (col.alias === 'entity') {
        if (cashFlowItem.entity_company) {
          value = `company-${cashFlowItem.entity_company}`;
        }
      } else if (col.alias === 'all_security') {
        value = cashFlowItem.security || null;
      }

      // If this is a virtual row, make it readonly
      const isVirtual = cashFlowItem.is_virtual || false;
      const readOnly = isVirtual || col.readOnly || false;

      if (col.alias === 'amount' && isVirtual) {
        if (cashFlowItem.transaction_type in { 'INVESTMENT': null }) {
          value = -Math.abs(cashFlowItem.amount);
        } else if (cashFlowItem.transaction_type in { 'REALIZED_PROCEEDS': null, 'UNREALIZED_VALUE': null }) {
          value = Math.abs(cashFlowItem.amount);
        }
      }

      // Get the row color class based on is_virtual (green = virtual/readonly, blue = manual/editable)
      const rowClass = getRowClassByVirtual(isVirtual);

      // The Amount cell carries exclusion state for the CflExclusionCheckbox viewer:
      //   - cell.enabled drives the checkbox + strikethrough
      //   - is_excludable=false (UNREALIZED_VALUE) tells the viewer to render plain
      //   - source_kind/source_id route the toggle to the right FO PATCH endpoint
      const isAmount = col.alias === 'amount';
      const isExcluded = !!cashFlowItem.is_excluded_from_calculations;
      const isExcludable = cashFlowItem.is_excludable !== false; // default true
      // Per-row currency from cap_table.company.irr_currency (always emitted by
      // backend; 'USD' fallback is defensive). Drives the AMOUNT cell's display
      // format and the inline currency-chip badge that replaces the dedicated
      // Currency column.
      const rowCurrency = cashFlowItem.currency ?? 'USD';
      // Pre-FX wiring on the AMOUNT cell. Persisted (id) non-virtual rows get
      // the reaction-bar trigger via CellAnnotationValueViewer. Virtual rows
      // (FO-derived) have no server-side CashFlowLedgerItem parent; new rows
      // (no id) likewise can't carry annotations until they're saved. The
      // currency chip still renders for those rows (via withMoneyChip) — the
      // bar/popout chrome is what gets gated, not the chip.
      const canAnnotateAmount = isAmount && !isVirtual && !!cashFlowItem.id;
      // The chip-wrapped Amount viewer is hoisted to module scope in
      // `CashFlowColumnConfig` (`AmountValueViewerWithChip`) so its component
      // identity is stable across parses — recreating it per row breaks
      // React reconciliation + memo on a busy ledger.
      const amountExtras = isAmount
        ? {
          enabled: !isExcluded,
          ignoreExpr: isExcluded,
          is_excludable: isExcludable,
          source_kind: cashFlowItem.source_kind,
          source_id: cashFlowItem.source_id,
          customFormat: { currency: { code: rowCurrency } },
          // Override the column's amount viewer with the chip-wrapped version
          // for ALL rows (virtual + manual). Annotation-eligible rows then
          // layer CellAnnotationValueViewer on top via the wiring below.
          valueViewer: AmountValueViewerWithChip,
          ...(canAnnotateAmount
            ? {
              // CellAnnotationValueViewer wraps the chip-decorated viewer so
              // the annotation chrome layers on top without losing the chip
              // or the exclusion checkbox.
              originalValueViewer: AmountValueViewerWithChip,
              valueViewer: CellAnnotationValueViewer,
              cellAnnotation: PRE_FX_CONFIG,
              cellAnnotationCellKey: buildCellKey(
                'cashflowledgeritem',
                cashFlowItem.id!,
                'amount',
                'pre_fx_currency'
              ),
              cellAnnotationBarCapabilities: MONEY_BAR_CAPABILITIES,
              defaultPreFxCurrency: rowCurrency,
              // Host currency is the row's IRR currency — same value the
              // server-side write path resolves from `company.irr_currency`.
              // Date is the transaction date already on the CFL row.
              preFxHostCurrency: rowCurrency,
              preFxTransactionDate: cashFlowItem.date_of_transaction ?? undefined,
              // Live preview writeback: the parent page (`CashFlows.tsx`)
              // implements the callback and patches the AMOUNT cell DIRECTLY
              // via the workbook's `setCells`, bypassing `cashFlows` state.
              // The cellKey here is `${columnLegend}${rowNumber}` (e.g. 'D5')
              // — the same address the framework uses internally.
              onPreFxHostPreview: onPreFxHostPreview
                ? (value: number | null) => onPreFxHostPreview(cellKey, value)
                : undefined,
              // Inline chip click routes through CellAnnotationValueViewer's
              // store subscriber to open the existing Pre-FX popout. Only
              // wired for persisted, non-virtual rows (the outer ternary
              // already gates via canAnnotateAmount + !!cashFlowItem.id).
              onChipClick: () => {
                useCellAnnotationStore.getState().openPopout(
                  buildCellKey(
                    'cashflowledgeritem',
                    cashFlowItem.id!,
                    'amount',
                    'pre_fx_currency'
                  )
                );
              },
            }
            : {}),
        }
        : {};

      cells[cellKey] = {
        ...col,
        value,
        entityId: cashFlowItem.id,
        key: cellKey,
        alias: col.alias,
        columnLegend,
        rowNumber,
        readOnly,
        is_virtual: isVirtual,
        expr: value?.toString() || '',
        className: addClassName(rowClass || '', col.className ?? '').trim() || undefined,
        ...amountExtras,
      } as CashFlowLedgerItem;
    });
  });

  return cells;
};
